import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class VoiceContacts extends LitElement {
  static properties = {
    contacts: { type: Array },
    selectedContact: { type: Object },
    searchQuery: { type: String },
    sortBy: { type: String },
    filterTag: { type: String },
    expandedCallId: { type: Number }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-header-left {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .page-subtitle {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      /* ─── Search & Filter Bar ─── */
      .filter-bar {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin-bottom: var(--space-8);
        flex-wrap: wrap;
      }

      .search-input-wrap {
        position: relative;
        flex: 1;
        min-width: 240px;
        max-width: 400px;
      }

      .search-input-wrap .input {
        padding-left: var(--space-10);
      }

      .search-input-wrap .search-icon {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
        pointer-events: none;
      }

      .filter-chips {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .filter-chip {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: var(--space-1) var(--space-3);
        border-radius: var(--radius-full);
        border: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .filter-chip:hover {
        border-color: var(--color-border-secondary);
        background: var(--color-bg-tertiary);
      }

      .filter-chip.active {
        border-color: var(--color-primary);
        background: var(--color-primary-light);
        color: var(--color-primary);
      }

      .sort-select {
        padding: var(--space-2) var(--space-3);
        padding-right: var(--space-8);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        background: var(--color-bg-secondary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2357534E' fill='none' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
        transition: all var(--transition-fast);
      }

      .sort-select:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      /* ─── Contact Table ─── */
      .table-container {
        border-radius: var(--radius-xl);
        border: 1px solid rgba(255, 255, 255, 0.35);
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
      }

      .avatar-circle {
        width: 32px;
        height: 32px;
        border-radius: var(--radius-full);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: var(--font-display);
        font-size: var(--text-xs);
        font-weight: var(--font-bold);
        color: var(--color-text-inverse);
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
      }

      .name-cell {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .name-text {
        font-family: var(--font-display);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }

      .phone-mono {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
      }

      .tag-chips {
        display: flex;
        gap: var(--space-1);
        flex-wrap: wrap;
      }

      .tag-chip {
        display: inline-flex;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: 0.625rem;
        font-weight: var(--font-semibold);
        letter-spacing: var(--tracking-wide);
      }

      .tag-vip { background: var(--color-primary-subtle); color: var(--color-primary); }
      .tag-new { background: var(--color-accent-subtle); color: var(--color-accent); }
      .tag-regular { background: var(--color-success-bg); color: var(--color-success-text); }
      .tag-lead { background: var(--color-warning-bg); color: var(--color-warning-text); }
      .tag-inactive { background: var(--color-bg-tertiary); color: var(--color-text-tertiary); }
      .tag-followup { background: var(--color-info-bg); color: var(--color-info-text); }

      .sentiment-dots {
        display: flex;
        align-items: center;
        gap: 3px;
      }

      .sentiment-dot {
        width: 8px;
        height: 8px;
        border-radius: var(--radius-full);
      }

      .sentiment-dot.positive { background: var(--color-success); }
      .sentiment-dot.negative { background: var(--color-error); }
      .sentiment-dot.neutral { background: var(--color-text-disabled); }

      .relative-time {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .row-action-btn {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        border-radius: var(--radius-md);
        color: var(--color-text-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .row-action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .row-actions {
        display: flex;
        gap: var(--space-1);
      }

      .total-calls-cell {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
      }

      /* ─── Detail View / Slide Over ─── */
      .detail-view {
        animation: slideUp var(--duration-slow) var(--ease-out) both;
      }

      .back-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-tertiary);
        background: none;
        border: none;
        cursor: pointer;
        padding: var(--space-1) 0;
        margin-bottom: var(--space-5);
        transition: color var(--transition-fast);
      }

      .back-btn:hover {
        color: var(--color-text-primary);
      }

      /* Detail Header */
      .contact-detail-header {
        display: flex;
        align-items: center;
        gap: var(--space-5);
        margin-bottom: var(--space-6);
        padding-bottom: var(--space-6);
        border-bottom: 1px solid var(--color-border-primary);
      }

      .detail-avatar {
        width: 64px;
        height: 64px;
        border-radius: var(--radius-full);
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-inverse);
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
      }

      .detail-header-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .detail-name {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .detail-meta {
        display: flex;
        align-items: center;
        gap: var(--space-4);
      }

      .detail-meta-item {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .detail-meta-item.mono {
        font-family: var(--font-mono);
      }

      /* Detail Sections */
      .detail-sections {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);
      }

      .detail-section-title {
        font-family: var(--font-display);
        font-size: var(--text-base);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        margin-bottom: var(--space-4);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .detail-section-title i {
        color: var(--color-text-tertiary);
      }

      /* Info Section */
      .info-tags {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }

      .info-tag {
        padding: var(--space-1) var(--space-3);
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
      }

      .notes-field {
        width: 100%;
        min-height: 80px;
        padding: var(--space-3);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        background: var(--color-bg-tertiary);
        resize: vertical;
        line-height: var(--leading-relaxed);
      }

      .notes-field:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
        background: var(--color-bg-secondary);
      }

      .custom-fields {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-3);
        margin-top: var(--space-4);
      }

      .custom-field {
        display: flex;
        flex-direction: column;
        gap: var(--space-0\.5);
      }

      .custom-field-label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
        font-weight: var(--font-medium);
      }

      .custom-field-value {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-primary);
        font-weight: var(--font-medium);
      }

      /* Call History */
      .call-history-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .call-history-item {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        background: var(--color-bg-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .call-history-item:hover {
        background: var(--color-bg-hover);
      }

      .call-date {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        min-width: 100px;
      }

      .call-duration {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        min-width: 50px;
      }

      .call-sentiment-icon {
        font-size: var(--text-sm);
      }

      .call-sentiment-icon.positive { color: var(--color-success); }
      .call-sentiment-icon.negative { color: var(--color-error); }
      .call-sentiment-icon.neutral { color: var(--color-text-tertiary); }

      .call-expand-icon {
        margin-left: auto;
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        transition: transform var(--transition-fast);
      }

      .call-expand-icon.open {
        transform: rotate(180deg);
      }

      .call-transcript-summary {
        padding: var(--space-3) var(--space-4);
        padding-left: calc(var(--space-4) + 100px + var(--space-4));
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        line-height: var(--leading-relaxed);
        background: var(--color-bg-secondary);
        border-radius: 0 0 var(--radius-md) var(--radius-md);
        border: 1px solid var(--color-border-subtle);
        border-top: none;
        animation: slideDown var(--duration-base) var(--ease-out) both;
      }

      /* AI Memory Section */
      .ai-memory-card {
        background: var(--color-info-light);
        border: 2px solid var(--color-info-bg);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        box-shadow: var(--shadow-sm);
      }

      .ai-memory-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }

      .ai-memory-header i {
        color: var(--color-info);
        font-size: var(--text-base);
      }

      .ai-memory-header span {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-info-text);
        letter-spacing: var(--tracking-tight);
      }

      .ai-memory-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .ai-memory-item {
        display: flex;
        gap: var(--space-3);
        align-items: flex-start;
      }

      .ai-memory-bullet {
        width: 6px;
        height: 6px;
        margin-top: 7px;
        border-radius: var(--radius-full);
        background: var(--color-info);
        flex-shrink: 0;
      }

      .ai-memory-text {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-info-text);
        line-height: var(--leading-relaxed);
      }

      /* ─── Detail View Card Overrides ─── */
      .detail-sections .card {
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-md);
        padding: var(--space-6);
      }

      /* ─── Responsive ─── */
      @media (max-width: 920px) {
        .filter-bar {
          flex-direction: column;
          align-items: stretch;
        }
        .search-input-wrap {
          max-width: none;
        }
        .custom-fields {
          grid-template-columns: 1fr;
        }
      }
    `
  ];

  constructor() {
    super();
    this.searchQuery = '';
    this.sortBy = 'name';
    this.filterTag = '';
    this.selectedContact = null;
    this.expandedCallId = null;

    const avatarColors = [
      '#2D8F6F', '#0EA5E9', '#16A34A', '#CA8A04', '#DC2626',
      '#6366F1', '#DB2777', '#059669', '#D97706', '#4F46E5'
    ];

    this.contacts = [
      {
        id: 1, name: 'Sarah Mitchell', phone: '+1 (555) 234-5678', email: 'sarah.mitchell@email.com',
        tags: ['VIP', 'Regular'], totalCalls: 24, lastCall: '2h ago', color: avatarColors[0],
        sentiments: ['positive', 'positive', 'neutral'],
        notes: 'Prefers morning calls. Very responsive to follow-ups. Has a dog named Max.',
        customFields: { 'Company': 'Acme Corp', 'Timezone': 'EST', 'Preferred Language': 'English', 'Account ID': 'ACM-1042' },
        callHistory: [
          { id: 101, date: 'Feb 8, 2026', duration: '3:24', status: 'completed', sentiment: 'positive', transcript: 'Discussed upcoming appointment. Sarah confirmed for Thursday at 2 PM. Mentioned she wants to add a teeth cleaning service.' },
          { id: 102, date: 'Feb 5, 2026', duration: '2:10', status: 'completed', sentiment: 'positive', transcript: 'Follow-up on January visit. Everything went well. Asked about new loyalty program and expressed interest.' },
          { id: 103, date: 'Jan 28, 2026', duration: '1:45', status: 'completed', sentiment: 'neutral', transcript: 'Routine check-in call. No issues reported. Confirmed address is still current.' }
        ],
        aiMemory: [
          'Prefers morning appointments between 9-11 AM',
          'VIP customer since 2023 with 24 interactions',
          'Works at Acme Corp, usually available during lunch breaks',
          'Has expressed interest in the premium loyalty program',
          'Previously rescheduled twice due to work conflicts — prefers 48h reminders'
        ]
      },
      {
        id: 2, name: 'James Rodriguez', phone: '+1 (555) 345-6789', email: 'j.rodriguez@email.com',
        tags: ['Lead', 'New'], totalCalls: 3, lastCall: '1d ago', color: avatarColors[1],
        sentiments: ['positive', 'neutral', 'positive'],
        notes: 'Interested in enterprise plan. Requested pricing deck.',
        customFields: { 'Company': 'TechFlow Inc', 'Timezone': 'PST', 'Preferred Language': 'English', 'Account ID': 'TFI-0087' },
        callHistory: [
          { id: 201, date: 'Feb 7, 2026', duration: '5:12', status: 'completed', sentiment: 'positive', transcript: 'Initial discovery call. James is evaluating solutions for his 50-person team. Budget approved for Q2. Wants a demo next week.' },
          { id: 202, date: 'Feb 3, 2026', duration: '1:30', status: 'completed', sentiment: 'neutral', transcript: 'Brief intro call. Collected requirements and scheduled follow-up.' },
          { id: 203, date: 'Jan 30, 2026', duration: '0:45', status: 'voicemail', sentiment: 'neutral', transcript: 'Left voicemail introducing our services and requesting callback.' }
        ],
        aiMemory: [
          'Decision maker for a 50-person team at TechFlow Inc',
          'Budget approved for Q2 2026 — hot lead',
          'Prefers email follow-ups after calls',
          'Comparing us with two competitors',
          'Needs enterprise SSO and API access'
        ]
      },
      {
        id: 3, name: 'Emily Chen', phone: '+1 (555) 456-7890', email: 'emily.chen@email.com',
        tags: ['Regular'], totalCalls: 12, lastCall: '3d ago', color: avatarColors[2],
        sentiments: ['positive', 'positive', 'positive'],
        notes: 'Long-time customer. Always positive interactions.',
        customFields: { 'Company': 'Self-employed', 'Timezone': 'CST', 'Preferred Language': 'English', 'Account ID': 'IND-2234' },
        callHistory: [
          { id: 301, date: 'Feb 5, 2026', duration: '2:50', status: 'completed', sentiment: 'positive', transcript: 'Renewed annual subscription. Very happy with the service. Referred a friend named David.' },
          { id: 302, date: 'Jan 15, 2026', duration: '4:20', status: 'completed', sentiment: 'positive', transcript: 'Support call about billing. Resolved quickly. Upgraded to premium tier.' }
        ],
        aiMemory: [
          'Loyal customer since 2024 — 100% positive sentiment',
          'Self-employed graphic designer',
          'Upgraded to premium tier in January 2026',
          'Has referred one new customer (David)',
          'Prefers text-based follow-ups over phone calls'
        ]
      },
      {
        id: 4, name: 'Michael Thompson', phone: '+1 (555) 567-8901', email: 'mthompson@email.com',
        tags: ['Follow-up'], totalCalls: 7, lastCall: '5d ago', color: avatarColors[3],
        sentiments: ['neutral', 'negative', 'neutral'],
        notes: 'Had a billing dispute in December. Issue resolved but follow up needed.',
        customFields: { 'Company': 'Thompson & Associates', 'Timezone': 'EST', 'Preferred Language': 'English', 'Account ID': 'TA-0553' },
        callHistory: [
          { id: 401, date: 'Feb 3, 2026', duration: '6:15', status: 'completed', sentiment: 'neutral', transcript: 'Follow-up on billing dispute. Michael confirmed the credit was applied. Still somewhat cautious but willing to continue service.' },
          { id: 402, date: 'Jan 20, 2026', duration: '8:30', status: 'completed', sentiment: 'negative', transcript: 'Billing dispute escalation. Michael was frustrated about double charge. Promised resolution within 48 hours.' }
        ],
        aiMemory: [
          'Had billing dispute in Dec 2025 — now resolved with credit applied',
          'Runs a law firm with 12 employees',
          'Sentiment recovering after the billing issue',
          'Prefers formal communication — address as Mr. Thompson',
          'Considering expanding service to his entire office if trust is rebuilt'
        ]
      },
      {
        id: 5, name: 'Lisa Park', phone: '+1 (555) 678-9012', email: 'lisa.park@email.com',
        tags: ['VIP', 'Lead'], totalCalls: 15, lastCall: '12h ago', color: avatarColors[4],
        sentiments: ['positive', 'positive', 'positive'],
        notes: 'High-value prospect. Interested in multi-location deal.',
        customFields: { 'Company': 'Park Dental Group', 'Timezone': 'CST', 'Preferred Language': 'English', 'Account ID': 'PDG-0011' },
        callHistory: [
          { id: 501, date: 'Feb 8, 2026', duration: '4:45', status: 'completed', sentiment: 'positive', transcript: 'Discussed pricing for 5-location rollout. Lisa is very interested and wants a proposal by Friday.' },
          { id: 502, date: 'Feb 6, 2026', duration: '3:00', status: 'completed', sentiment: 'positive', transcript: 'Demo call for Park Dental Group. Showed multi-location features. Very impressed with reporting.' }
        ],
        aiMemory: [
          'Runs 5 dental office locations across the Midwest',
          'Looking for unified communication platform for all locations',
          'Budget range: $2K-5K/month — high-value prospect',
          'Decision timeline: End of February 2026',
          'Key requirements: multi-location reporting, HIPAA compliance'
        ]
      },
      {
        id: 6, name: 'David Kim', phone: '+1 (555) 789-0123', email: 'david.kim@email.com',
        tags: ['New'], totalCalls: 1, lastCall: '1w ago', color: avatarColors[5],
        sentiments: ['neutral'],
        notes: 'Referred by Emily Chen. First contact made.',
        customFields: { 'Company': 'Kim Studios', 'Timezone': 'PST', 'Preferred Language': 'English', 'Account ID': 'KS-0401' },
        callHistory: [
          { id: 601, date: 'Feb 1, 2026', duration: '2:10', status: 'completed', sentiment: 'neutral', transcript: 'Introductory call. David was referred by Emily Chen. Interested in basic plan. Wants to try free trial first.' }
        ],
        aiMemory: [
          'Referred by existing customer Emily Chen',
          'Freelance photographer — small business',
          'Interested in basic plan and free trial',
          'Tech-savvy, prefers self-service setup',
          'No urgency — exploring options'
        ]
      },
      {
        id: 7, name: 'Rachel Adams', phone: '+1 (555) 890-1234', email: 'rachel.a@email.com',
        tags: ['Regular', 'Follow-up'], totalCalls: 9, lastCall: '4d ago', color: avatarColors[6],
        sentiments: ['positive', 'neutral', 'negative'],
        notes: 'Experienced a service outage last week. Needs assurance.',
        customFields: { 'Company': 'Adams Marketing', 'Timezone': 'EST', 'Preferred Language': 'English', 'Account ID': 'AM-1190' },
        callHistory: [
          { id: 701, date: 'Feb 4, 2026', duration: '5:30', status: 'completed', sentiment: 'positive', transcript: 'Apologized for outage and explained root cause. Rachel appreciated the transparency and accepted the service credit.' },
          { id: 702, date: 'Feb 1, 2026', duration: '3:45', status: 'completed', sentiment: 'negative', transcript: 'Rachel called about the service outage. Very frustrated about lost leads during downtime. Escalated to account manager.' }
        ],
        aiMemory: [
          'Experienced service outage on Jan 31 — received credit',
          'Runs a marketing agency with 8 clients',
          'Service is mission-critical for her lead generation',
          'Sentiment recovering after outage resolution',
          'Needs 99.9% uptime SLA for renewal discussion'
        ]
      },
      {
        id: 8, name: 'Tom Bradley', phone: '+1 (555) 901-2345', email: 'tom.bradley@email.com',
        tags: ['Inactive'], totalCalls: 2, lastCall: '3w ago', color: avatarColors[7],
        sentiments: ['neutral', 'neutral'],
        notes: 'Signed up but never fully onboarded. Needs re-engagement.',
        customFields: { 'Company': 'Bradley Consulting', 'Timezone': 'MST', 'Preferred Language': 'English', 'Account ID': 'BC-0782' },
        callHistory: [
          { id: 801, date: 'Jan 18, 2026', duration: '1:50', status: 'completed', sentiment: 'neutral', transcript: 'Attempted onboarding walkthrough. Tom was distracted and asked to reschedule. Never called back.' },
          { id: 802, date: 'Jan 10, 2026', duration: '2:30', status: 'completed', sentiment: 'neutral', transcript: 'Welcome call for new sign-up. Tom seemed interested but mentioned he is very busy this month.' }
        ],
        aiMemory: [
          'Signed up Jan 10 but has not completed onboarding',
          'Consultant — very busy schedule',
          'Needs a condensed 15-min onboarding session',
          'May respond better to email + video tutorial approach',
          'Risk of churn if not re-engaged within 2 weeks'
        ]
      },
      {
        id: 9, name: 'Amanda Foster', phone: '+1 (555) 012-3456', email: 'a.foster@email.com',
        tags: ['VIP', 'Regular'], totalCalls: 31, lastCall: '6h ago', color: avatarColors[8],
        sentiments: ['positive', 'positive', 'positive'],
        notes: 'Power user. Beta tester. Provides excellent feedback.',
        customFields: { 'Company': 'Foster Health', 'Timezone': 'EST', 'Preferred Language': 'English', 'Account ID': 'FH-0023' },
        callHistory: [
          { id: 901, date: 'Feb 8, 2026', duration: '7:20', status: 'completed', sentiment: 'positive', transcript: 'Beta feedback session. Amanda loves the new scheduling feature. Suggested adding calendar sync with Google. Willing to do a case study.' },
          { id: 902, date: 'Feb 6, 2026', duration: '4:15', status: 'completed', sentiment: 'positive', transcript: 'Discussed expansion to second office location. Wants same setup mirrored. Projected go-live: March 1.' },
          { id: 903, date: 'Feb 2, 2026', duration: '3:00', status: 'completed', sentiment: 'positive', transcript: 'Routine check-in. All systems running smoothly. Amanda referred two new businesses to us.' }
        ],
        aiMemory: [
          'Top customer — 31 calls, 100% positive sentiment',
          'Beta tester and product advocate',
          'Expanding to second office location in March 2026',
          'Has referred 2 new businesses',
          'Interested in calendar sync with Google — feature request logged',
          'Willing to participate in case study for marketing'
        ]
      },
      {
        id: 10, name: 'Carlos Mendez', phone: '+1 (555) 123-4567', email: 'carlos.m@email.com',
        tags: ['Lead'], totalCalls: 4, lastCall: '2d ago', color: avatarColors[9],
        sentiments: ['positive', 'neutral', 'positive'],
        notes: 'Evaluating for restaurant chain. Needs bilingual support.',
        customFields: { 'Company': 'Mendez Restaurant Group', 'Timezone': 'CST', 'Preferred Language': 'Spanish/English', 'Account ID': 'MRG-0156' },
        callHistory: [
          { id: 1001, date: 'Feb 6, 2026', duration: '6:10', status: 'completed', sentiment: 'positive', transcript: 'Detailed demo of bilingual features. Carlos was very impressed with Spanish language support. Wants to pilot at one location first.' },
          { id: 1002, date: 'Feb 2, 2026', duration: '3:40', status: 'completed', sentiment: 'neutral', transcript: 'Requirements gathering. Carlos needs bilingual (EN/ES) voice agents, reservation management, and integration with their POS system.' },
          { id: 1003, date: 'Jan 28, 2026', duration: '4:00', status: 'completed', sentiment: 'positive', transcript: 'Intro call. Carlos runs 8 restaurants. Currently using manual phone system. Interested in AI voice for reservations.' }
        ],
        aiMemory: [
          'Operates 8 restaurants across Texas and New Mexico',
          'Needs bilingual (English/Spanish) voice support',
          'Key use case: reservation management and order confirmations',
          'Wants POS integration with their Toast system',
          'Planning a single-location pilot before chain-wide rollout',
          'Decision timeline: pilot by end of February'
        ]
      }
    ];
  }

  _getInitials(name) {
    return name.split(' ').map(p => p[0]).join('').toUpperCase();
  }

  _getTagClass(tag) {
    const map = {
      'VIP': 'tag-vip',
      'New': 'tag-new',
      'Regular': 'tag-regular',
      'Lead': 'tag-lead',
      'Inactive': 'tag-inactive',
      'Follow-up': 'tag-followup'
    };
    return map[tag] || 'tag-regular';
  }

  _filteredContacts() {
    let list = [...this.contacts];

    // Search
    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        c.email.toLowerCase().includes(q)
      );
    }

    // Tag filter
    if (this.filterTag) {
      list = list.filter(c => c.tags.includes(this.filterTag));
    }

    // Sort
    if (this.sortBy === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (this.sortBy === 'lastCall') {
      // Approximate: keep original order (already mock-sorted)
    } else if (this.sortBy === 'totalCalls') {
      list.sort((a, b) => b.totalCalls - a.totalCalls);
    }

    return list;
  }

  _selectContact(c) {
    this.selectedContact = { ...c };
    this.expandedCallId = null;
  }

  _backToList() {
    this.selectedContact = null;
  }

  _onSearch(e) {
    this.searchQuery = e.target.value;
  }

  _onSort(e) {
    this.sortBy = e.target.value;
  }

  _toggleTag(tag) {
    this.filterTag = this.filterTag === tag ? '' : tag;
  }

  _toggleCallExpand(callId) {
    this.expandedCallId = this.expandedCallId === callId ? null : callId;
  }

  // ─── List View ───
  _renderListView() {
    const filtered = this._filteredContacts();
    const allTags = ['VIP', 'Regular', 'Lead', 'New', 'Follow-up', 'Inactive'];

    return html`
      <div class="page-header">
        <div class="page-header-left">
          <h1 class="page-title">Contacts</h1>
          <p class="page-subtitle">Manage your phonebook and track customer interactions</p>
        </div>
        <div class="header-actions">
          <button class="btn btn-secondary">
            <i class="fa-sharp fa-light fa-file-import"></i>
            Import CSV
          </button>
          <button class="btn btn-primary">
            <i class="fa-sharp fa-light fa-user-plus"></i>
            Add Contact
          </button>
        </div>
      </div>

      <!-- Filter Bar -->
      <div class="filter-bar">
        <div class="search-input-wrap">
          <i class="fa-sharp fa-light fa-magnifying-glass search-icon"></i>
          <input
            class="input"
            type="text"
            placeholder="Search by name, phone, or email..."
            .value=${this.searchQuery}
            @input=${this._onSearch}
          />
        </div>

        <div class="filter-chips">
          ${allTags.map(tag => html`
            <span
              class="filter-chip ${this.filterTag === tag ? 'active' : ''}"
              @click=${() => this._toggleTag(tag)}
            >${tag}</span>
          `)}
        </div>

        <select class="sort-select" .value=${this.sortBy} @change=${this._onSort}>
          <option value="name">Sort: Name</option>
          <option value="lastCall">Sort: Last Call</option>
          <option value="totalCalls">Sort: Total Calls</option>
        </select>
      </div>

      <!-- Contact Table -->
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone Number</th>
              <th>Email</th>
              <th>Tags</th>
              <th>Total Calls</th>
              <th>Last Call</th>
              <th>Sentiment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            ${filtered.map(c => html`
              <tr>
                <td>
                  <div class="name-cell">
                    <div class="avatar-circle" style="background: ${c.color}">
                      ${this._getInitials(c.name)}
                    </div>
                    <span class="name-text">${c.name}</span>
                  </div>
                </td>
                <td><span class="phone-mono">${c.phone}</span></td>
                <td style="font-size: var(--text-sm); color: var(--color-text-secondary);">${c.email}</td>
                <td>
                  <div class="tag-chips">
                    ${c.tags.map(tag => html`
                      <span class="tag-chip ${this._getTagClass(tag)}">${tag}</span>
                    `)}
                  </div>
                </td>
                <td><span class="total-calls-cell">${c.totalCalls}</span></td>
                <td><span class="relative-time">${c.lastCall}</span></td>
                <td>
                  <div class="sentiment-dots">
                    ${c.sentiments.map(s => html`
                      <span class="sentiment-dot ${s}"></span>
                    `)}
                  </div>
                </td>
                <td>
                  <div class="row-actions">
                    <button class="row-action-btn" title="Call" @click=${(e) => e.stopPropagation()}>
                      <i class="fa-sharp fa-light fa-phone"></i>
                    </button>
                    <button class="row-action-btn" title="View Details" @click=${() => this._selectContact(c)}>
                      <i class="fa-sharp fa-light fa-arrow-right"></i>
                    </button>
                  </div>
                </td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  // ─── Detail View ───
  _renderDetailView() {
    const c = this.selectedContact;
    return html`
      <div class="detail-view">
        <button class="back-btn" @click=${this._backToList}>
          <i class="fa-sharp fa-light fa-arrow-left"></i>
          Back to Contacts
        </button>

        <!-- Header -->
        <div class="contact-detail-header">
          <div class="detail-avatar" style="background: ${c.color}">
            ${this._getInitials(c.name)}
          </div>
          <div class="detail-header-info">
            <div class="detail-name">${c.name}</div>
            <div class="detail-meta">
              <span class="detail-meta-item mono">
                <i class="fa-sharp fa-light fa-phone"></i>
                ${c.phone}
              </span>
              <span class="detail-meta-item">
                <i class="fa-sharp fa-light fa-envelope"></i>
                ${c.email}
              </span>
            </div>
          </div>
          <button class="btn btn-secondary btn-sm">
            <i class="fa-sharp fa-light fa-pen"></i>
            Edit
          </button>
        </div>

        <div class="detail-sections">
          <!-- Info Section -->
          <div class="card">
            <div class="detail-section-title">
              <i class="fa-sharp fa-light fa-circle-info"></i>
              Contact Info
            </div>

            <div class="info-tags">
              ${c.tags.map(tag => html`
                <span class="info-tag ${this._getTagClass(tag)}">${tag}</span>
              `)}
            </div>

            <textarea
              class="notes-field"
              placeholder="Add notes..."
              .value=${c.notes || ''}
              @input=${() => {}}
            ></textarea>

            <div class="custom-fields">
              ${Object.entries(c.customFields || {}).map(([label, value]) => html`
                <div class="custom-field">
                  <span class="custom-field-label">${label}</span>
                  <span class="custom-field-value">${value}</span>
                </div>
              `)}
            </div>
          </div>

          <!-- Call History Section -->
          <div class="card">
            <div class="detail-section-title">
              <i class="fa-sharp fa-light fa-clock-rotate-left"></i>
              Call History
              <span class="badge badge-neutral" style="margin-left: auto;">${c.callHistory.length} calls</span>
            </div>

            <div class="call-history-list">
              ${c.callHistory.map(call => html`
                <div>
                  <div class="call-history-item" @click=${() => this._toggleCallExpand(call.id)}>
                    <span class="call-date">${call.date}</span>
                    <span class="call-duration">${call.duration}</span>
                    <span class="badge ${call.status === 'completed' ? 'badge-success' : call.status === 'voicemail' ? 'badge-warning' : 'badge-error'}">
                      ${call.status}
                    </span>
                    <i class="call-sentiment-icon ${call.sentiment} fa-sharp fa-light ${call.sentiment === 'positive' ? 'fa-face-smile' : call.sentiment === 'negative' ? 'fa-face-frown' : 'fa-face-meh'}"></i>
                    <i class="fa-sharp fa-light fa-chevron-down call-expand-icon ${this.expandedCallId === call.id ? 'open' : ''}"></i>
                  </div>
                  ${this.expandedCallId === call.id ? html`
                    <div class="call-transcript-summary">
                      <strong>Summary:</strong> ${call.transcript}
                    </div>
                  ` : ''}
                </div>
              `)}
            </div>
          </div>

          <!-- AI Memory Section -->
          <div class="ai-memory-card">
            <div class="ai-memory-header">
              <i class="fa-sharp-duotone fa-solid fa-brain-circuit"></i>
              <span>What we know about ${c.name.split(' ')[0]}</span>
            </div>
            <div class="ai-memory-list">
              ${(c.aiMemory || []).map(item => html`
                <div class="ai-memory-item">
                  <span class="ai-memory-bullet"></span>
                  <span class="ai-memory-text">${item}</span>
                </div>
              `)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      ${iconStyles}
      ${this.selectedContact
        ? this._renderDetailView()
        : this._renderListView()}
    `;
  }
}

customElements.define('voice-contacts', VoiceContacts);
